import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { firstValueFrom, map, of, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { UserService } from '../../shared/services/user.service';
import { BocClient, FieldOfOrderDraft, OrderDraft, ProductFamily, RequestOrder } from '../models/eorder';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { ORDER_SERVER_URL } from '../../config';
import { OrderBy } from '../../shared/utils/constant';

import { ProductService } from './product.api.service';

const DRAFT_ORDER_LINES_URL = `${ ORDER_SERVER_URL }/v1/draftOrders`;
const DRAFT_ORDER_LINES_URL_V2 = `${ ORDER_SERVER_URL }/v2/draftOrders`;
const EORDER_SUBMIT_URL = `${ ORDER_SERVER_URL }/v1/submitOrders`;

@Injectable({ providedIn: 'root' })
export class EorderApiService {
	productFormGroup?: FormGroup;
	filteredSoldtoShiptoList?: BocClient[];
	productFamilies?: ProductFamily[];
	isDefaultSoldTo = false;
	private configurationFieldNamesLoaded = false;

	constructor(
		private readonly userService: UserService,
		private readonly productService: ProductService,
		private readonly http: HttpClient
	) {}

	getBocs() {
		return this.userService.getSoldToRelationshipGroupsTransac('EORDR');
	}

	loadConfigurationFieldNames() {
		if (this.configurationFieldNamesLoaded) {
			return of(false);
		}
		return this.productService.loadFieldNameTranslations(false).pipe(tap(
			() => {
				this.configurationFieldNamesLoaded = true;
			}
		));
	}

	async deleteDraftOrders(orderId: number) {
		const url = new URL(`${ DRAFT_ORDER_LINES_URL }/${ orderId }`);
		await firstValueFrom(this.http.delete(url.toString()));
	}

	getDraftOrders(
		fields: readonly FieldOfOrderDraft[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof OrderDraft>[] | OrderBy<keyof OrderDraft>,
		onlyDistinct: boolean
	) {
		const url = new URL(DRAFT_ORDER_LINES_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		params.append('distinct', String(onlyDistinct));

		return this.http.get<PaginatedResult<OrderDraft>>(url.toString());
	}

	getDraftOrderCount(onlyCount: boolean) {
		const url = new URL(DRAFT_ORDER_LINES_URL);
		const params = url.searchParams;
		params.append('onlyCount', String(onlyCount));
		return this.http.get<PaginatedResult<OrderDraft>>(url.toString());
	}

	getEordrDraft(
		orderId: number
	) {
		const url = new URL(`${ DRAFT_ORDER_LINES_URL }/${ orderId }`);
		return this.http.get<RequestOrder>(url.toString()).pipe(map(order => order));
	}

	createOrder(
		order: RequestOrder
	) {
		const url = new URL(DRAFT_ORDER_LINES_URL_V2);
		return this.http.post<RequestOrder>(
			url.toString(),
			order
		);
	}

	submitOrder(order: RequestOrder) {
		const url = new URL(EORDER_SUBMIT_URL);
		return this.http.post<RequestOrder>(
			url.toString(),
			order
		);
	}
}
